import { useMemo, useCallback } from "react";
import {
  AVAILABLE_USER_PLATFORM_ROLE,
  LightService,
  PlatformUser,
} from "./list-users";
import "./list-user.css";
import {
  faCog,
  faEye,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEthosModal } from "../../../contexts/ethos-modal-context";

interface UserBoxProps {
  users: PlatformUser[];
  headerTitle: string;
  role: AVAILABLE_USER_PLATFORM_ROLE;
  services: LightService[];
  doUserEdit: (userId: string, user: PlatformUser) => Promise<void>;
  doUserDelete?: (userId: string, user: PlatformUser) => Promise<void>;
  doUserRestore?: (userId: string, user: PlatformUser) => Promise<void>;
  isAdmin?: boolean;
  currentUsername: string;
}

export const UserBox = ({
  users,
  headerTitle,
  role,
  services,
  doUserEdit,
  doUserDelete,
  doUserRestore,
  isAdmin,
  currentUsername,
}: UserBoxProps) => {
  const { openUserFeeInfoContent } = useEthosModal();

  const roleUsers = useMemo(() => {
    return users
      .filter((u) => u.role === role)
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.first_name.localeCompare(b.first_name);
      });
  }, [users, role]);

  const activeCount = useMemo(
    () => roleUsers.filter((u) => u.isActive).length,
    [roleUsers],
  );

  const inactiveCount = roleUsers.length - activeCount;

  const handleOpenFeeInfo = useCallback(
    (u: PlatformUser) => {
      openUserFeeInfoContent(
        `${u.first_name} ${u.last_name}`,
        u,
        services,
        doUserEdit,
        isAdmin,
      );
    },
    [openUserFeeInfoContent, services, doUserEdit, isAdmin],
  );

  return (
    <div className="userBox">
      <div className="userBox__title">
        {headerTitle} ({roleUsers.length})
        <div className="userBox__subtitle">
          <span>ATTIVI {activeCount}</span>
          <span>DISABILITATI {inactiveCount}</span>
        </div>
      </div>

      <div className="userBox__content">
        {roleUsers.map((u) => (
          <UserRow
            key={u._id}
            user={u}
            services={services}
            doUserDelete={doUserDelete}
            doUserRestore={doUserRestore}
            currentUsername={currentUsername}
            onOpenFeeInfo={handleOpenFeeInfo}
          />
        ))}
      </div>
    </div>
  );
};

// Componente separato per evitare re-render inutili
const UserRow = ({
  user,
  doUserDelete,
  doUserRestore,
  currentUsername,
  onOpenFeeInfo,
}: {
  user: PlatformUser;
  services: LightService[];
  doUserDelete?: (userId: string, user: PlatformUser) => Promise<void>;
  doUserRestore?: (userId: string, user: PlatformUser) => Promise<void>;
  currentUsername: string;
  onOpenFeeInfo: (u: PlatformUser) => void;
}) => (
  <div className="userRow__box">
    <span className={`userRow__name${!user.isActive ? " inactive" : ""}`}>
      {user.first_name.toLowerCase()} {user.last_name.toLowerCase()}
    </span>
    <div className="userRow__actions">
      {user.isActive ? (
        <>
          <a href={`/users/info/${user.username}`}>
            <FontAwesomeIcon icon={faEye} />
          </a>
          <FontAwesomeIcon icon={faCog} onClick={() => onOpenFeeInfo(user)} />
          {doUserDelete && user.username !== currentUsername && (
            <FontAwesomeIcon
              icon={faTrash}
              className="userRow__action--delete"
              title="Elimina utente"
              onClick={() => doUserDelete(user._id, user)}
            />
          )}
        </>
      ) : (
        doUserRestore && (
          <FontAwesomeIcon
            icon={faUndo}
            className="userRow__action--restore"
            title="Riabilita utente"
            onClick={() => doUserRestore(user._id, user)}
          />
        )
      )}
    </div>
  </div>
);
